import type { ReactNode } from 'react';

import { REGEXP_WRAPPER } from './constants';
import { interpolatePlaceholders } from './interpolatePlaceholders';
import type { ElementOrStringPlaceholderMap, WrapperMap } from './types';

interface WrapperSegment {
  readonly name: string;
  readonly content: string;
}

type Segment = string | WrapperSegment;

/**
 * Splits a template into text gaps and wrapper descriptors. Uses the global
 * REGEXP_WRAPPER regex, so this loop must not call out to external code —
 * global regexes share mutable lastIndex state, and any re-entrant call
 * would corrupt it.
 */
const splitWrapperSegments = (str: string): Segment[] => {
  const segments: Segment[] = [];
  let previousMatchEnd = 0;
  REGEXP_WRAPPER.lastIndex = 0;
  let match = REGEXP_WRAPPER.exec(str);

  while (match) {
    const textBefore = str.slice(previousMatchEnd, match.index);
    if (textBefore) {
      segments.push(textBefore);
    }
    segments.push({ name: match[1], content: match[2] });
    previousMatchEnd = REGEXP_WRAPPER.lastIndex;
    match = REGEXP_WRAPPER.exec(str);
  }

  const trailing = str.slice(previousMatchEnd);
  if (trailing) {
    segments.push(trailing);
  }

  return segments;
};

/**
 * Resolves wrappers and placeholders in two passes:
 * 1. Parse: split template into text and wrapper segments (pure, no callbacks)
 * 2. Evaluate: resolve placeholders and call wrapper functions
 *
 * Wrapper functions may safely call interpolateWithWrappers internally
 * (e.g. nested translations) because the regex loop has already completed.
 */
export const interpolateWithWrappers = (
  str: string,
  locale: string,
  placeholders: ElementOrStringPlaceholderMap,
  wrapperFunctions: WrapperMap
): string | ReactNode[] => {
  const segments = splitWrapperSegments(str);

  // Separate string-only placeholders for use inside wrapper content
  // (wrapper functions expect string arguments)
  const stringPlaceholders: Partial<Record<string, string | number>> = {};
  for (const [name, value] of Object.entries(placeholders)) {
    if (typeof value !== 'object') {
      stringPlaceholders[name] = value;
    }
  }

  const result: ReactNode[] = [];
  let hasElement = false;

  for (const segment of segments) {
    if (typeof segment === 'string') {
      // Text gap — resolve all placeholders (including ReactElements)
      const interpolated = interpolatePlaceholders(segment, locale, placeholders);

      if (typeof interpolated === 'string') {
        if (interpolated) {
          result.push(interpolated);
        }
      } else {
        hasElement = true;
        result.push(...interpolated);
      }
    } else {
      // Wrapper — resolve content with string-only placeholders, then wrap
      const wrapperFn = wrapperFunctions[segment.name] as ((str: string) => ReactNode) | undefined;
      const interpolatedContent = interpolatePlaceholders(segment.content.trim(), locale, stringPlaceholders);

      // istanbul ignore else: defensive — unrecognized wrapper fallback
      if (wrapperFn) {
        hasElement = true;
        result.push(wrapperFn(interpolatedContent));
      } else if (interpolatedContent) {
        result.push(interpolatedContent);
      }
    }
  }

  if (!hasElement) {
    return (result as string[]).join('');
  }

  return result;
};
